import React from 'react';
import { Plus, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDateIST } from '@/utils/date';
import WalkinSection from './WalkinSection';

const RELATIONSHIPS = [
  'spouse', 'son', 'daughter', 'father', 'mother',
  'brother', 'sister', 'guardian', 'other',
];

export default function WalkinIdentitySection({ form }) {
  const {
    matches, mode, setMode, selectedExisting, applyPick,
    pName, setPName, pAge, setPAge, pGender, setPGender,
    pRelationship, setPRelationship, setSelectedExisting,
  } = form;
  const locked = mode === 'pick' && !!selectedExisting;

  return (
    <WalkinSection
      step={2}
      title="Patient details"
      hint="Confirm identity, or register a new visitor / family member."
      testId="walkin-identity-section"
    >
      {matches.length > 0 && (
        <div className="border border-[#D5DEE8] rounded-xl overflow-hidden" data-testid="rec-family-card">
          <div className="bg-[#F8FAFC] border-b border-[#D5DEE8] px-3 py-2 flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-[#F15A22]" />
            <span className="text-xs font-semibold text-[#071B34]">
              {matches.length} match{matches.length > 1 ? 'es' : ''} found — select who is here
            </span>
          </div>
          <div className="divide-y divide-[#E8EEF5]">
            {matches.map((p) => {
              const active = selectedExisting?.id === p.id;
              return (
                <button key={p.id} type="button" onClick={() => applyPick(p)}
                  className={`w-full text-left px-3 py-2.5 transition ${active ? 'bg-[#FFF7F3]' : 'hover:bg-[#F8FAFC]'}`}
                  data-testid={`rec-family-row-${p.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[#071B34] truncate">{p.name}</span>
                    {p.patient_number && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-800">
                        {p.patient_number}
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                      p.is_primary ? 'bg-sky-50 text-sky-800 border-sky-200' : 'bg-zinc-50 text-zinc-600 border-zinc-200'
                    }`}>
                      {p.is_primary ? 'Primary' : p.relationship}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#5A6B7C] mt-0.5">
                    {p.age ? `${p.age}y · ` : ''}{p.gender || '—'}
                    {p.email ? ` · ${p.email}` : ''}
                    {' · last visit: '}
                    {p.last_visit_at ? formatDateIST(p.last_visit_at) : 'never'}
                  </div>
                </button>
              );
            })}
          </div>
          <button type="button"
            onClick={() => { setMode('add_member'); setSelectedExisting(null); setPRelationship('spouse'); }}
            className="w-full text-left px-3 py-2 text-xs font-semibold text-[#F15A22] hover:bg-[#FFF7F3] border-t border-[#D5DEE8] flex items-center gap-1"
            data-testid="rec-add-member">
            <Plus className="h-3 w-3" /> Register another family member on this phone
          </button>
        </div>
      )}

      {(mode === 'new' || mode === 'add_member' || locked) && (
        <div className="space-y-3" data-testid="rec-new-patient-form">
          {locked && (
            <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Using existing patient record — fields are filled from the database.
            </p>
          )}
          <div className="space-y-1.5">
            <Label>Full name *</Label>
            <Input value={pName} onChange={(e) => setPName(e.target.value)} disabled={locked} data-testid="rec-pname" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Age</Label>
              <Input value={pAge} onChange={(e) => setPAge(e.target.value)} type="number" disabled={locked} data-testid="rec-page" />
            </div>
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Select value={pGender} onValueChange={setPGender} disabled={locked}>
                <SelectTrigger data-testid="rec-pgender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {mode === 'add_member' && (
            <div className="space-y-1.5">
              <Label>Relationship to primary</Label>
              <Select value={pRelationship} onValueChange={setPRelationship}>
                <SelectTrigger data-testid="rec-prelationship"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map((r) => (
                    <SelectItem key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </WalkinSection>
  );
}
